/**
 * Pure helpers for `categoryLabelMode="auto"`: DOM truncation measurement and
 * hysteresis-based mode resolution between truncate, angle-30, and angle-45.
 */

import type { EffectiveCategoryLabelMode } from '../shared/chart-tokens';

import { CHART_X_AXIS_TRUNCATE_PROBE_SELECTOR } from './bar-chart-tick-utils';

export const AUTO_LABEL_TRUNCATE_ESCALATE_MIN = 3;
export const AUTO_LABEL_TRUNCATE_DEESCALATE_MAX = 1;
export const AUTO_LABEL_ANGLE_45_ESCALATE_LEFT_OVERFLOW_PX = 32;
export const AUTO_LABEL_ANGLE_45_DEESCALATE_LEFT_OVERFLOW_PX = 20;
export const AUTO_LABEL_DIRECT_TO_ANGLE_45_RATIO = 1.5;
const TRUNCATION_EPSILON_PX = 1;

interface TruncationMeasurement {
  truncatedCount: number;
  maxOverflowRatio: number;
}

/** Reads horizontal probe nodes and aggregates truncation counts and severity. */
const measureTruncation = (chartRoot: HTMLElement): TruncationMeasurement => {
  const labels = Array.from(
    chartRoot.querySelectorAll(CHART_X_AXIS_TRUNCATE_PROBE_SELECTOR)
  );
  const htmlLabels = labels.filter(
    (node): node is HTMLElement => node instanceof HTMLElement
  );

  return htmlLabels.reduce<TruncationMeasurement>(
    (current, label) => {
      const { clientWidth, scrollWidth } = label;

      if (clientWidth <= 0) {
        return current;
      }

      const overflowRatio = scrollWidth / clientWidth;
      const isTruncated = scrollWidth > clientWidth + TRUNCATION_EPSILON_PX;

      return {
        truncatedCount: current.truncatedCount + (isTruncated ? 1 : 0),
        maxOverflowRatio: Math.max(current.maxOverflowRatio, overflowRatio),
      };
    },
    { truncatedCount: 0, maxOverflowRatio: 1 }
  );
};

export const countTruncatedCategoryLabels = (chartRoot: HTMLElement): number =>
  measureTruncation(chartRoot).truncatedCount;

export const getTruncationSeverity = (chartRoot: HTMLElement): number =>
  measureTruncation(chartRoot).maxOverflowRatio;

export const hasTruncationProbeLabels = (chartRoot: HTMLElement): boolean =>
  chartRoot.querySelector(CHART_X_AXIS_TRUNCATE_PROBE_SELECTOR) !== null;

interface ResolveAutoLabelModeParams {
  currentMode: EffectiveCategoryLabelMode;
  truncatedCount: number;
  leftOverflowPx: number;
  maxOverflowRatio: number;
}

/**
 * Applies hysteresis thresholds to pick the next effective label mode.
 */
export const resolveAutoLabelMode = ({
  currentMode,
  truncatedCount,
  leftOverflowPx,
  maxOverflowRatio,
}: ResolveAutoLabelModeParams): EffectiveCategoryLabelMode => {
  if (currentMode === 'wrap') {
    return currentMode;
  }

  if (currentMode === 'truncate') {
    if (truncatedCount < AUTO_LABEL_TRUNCATE_ESCALATE_MIN) {
      return 'truncate';
    }

    if (maxOverflowRatio >= AUTO_LABEL_DIRECT_TO_ANGLE_45_RATIO) {
      return 'angle-45';
    }

    return 'angle-30';
  }

  if (currentMode === 'angle-45') {
    if (leftOverflowPx < AUTO_LABEL_ANGLE_45_DEESCALATE_LEFT_OVERFLOW_PX) {
      return 'angle-30';
    }

    return 'angle-45';
  }

  if (leftOverflowPx > AUTO_LABEL_ANGLE_45_ESCALATE_LEFT_OVERFLOW_PX) {
    return 'angle-45';
  }

  if (truncatedCount <= AUTO_LABEL_TRUNCATE_DEESCALATE_MAX) {
    return 'truncate';
  }

  return 'angle-30';
};
